import { QuickPanelDrawerFooter } from '@zrsystem/ui';
import {
  createContext,
  useCallback,
  useContext,
  useId,
  useLayoutEffect,
  useMemo,
  useState,
  type Context,
  type ReactNode,
} from 'react';

interface QuickPanelFooterSlotContextValue {
  registerFooter: (ownerId: string, footer: ReactNode | null) => void;
}

const QUICK_PANEL_FOOTER_SLOT_CONTEXT = Symbol.for('zrsystem.quickPanelFooterSlotContext');

type QuickPanelFooterSlotGlobal = typeof globalThis & {
  [QUICK_PANEL_FOOTER_SLOT_CONTEXT]?: Context<QuickPanelFooterSlotContextValue | null>;
};

/**
 * One context for every evaluation of this module. Vite can hot-reload a surface frame
 * without re-running QuickPanelHost; a fresh createContext() then makes useContext return
 * null and the drawer footer disappears while the presenter still passes it.
 */
function readQuickPanelFooterSlotContext(): Context<QuickPanelFooterSlotContextValue | null> {
  const host = globalThis as QuickPanelFooterSlotGlobal;
  const existing = host[QUICK_PANEL_FOOTER_SLOT_CONTEXT];
  if (existing !== undefined) {
    return existing;
  }

  const created = createContext<QuickPanelFooterSlotContextValue | null>(null);
  host[QUICK_PANEL_FOOTER_SLOT_CONTEXT] = created;
  return created;
}

const QuickPanelFooterSlotContext = readQuickPanelFooterSlotContext();

interface FooterRegistration {
  ownerId: string;
  node: ReactNode;
}

export function QuickPanelFooterSlotProvider({ children }: { children: ReactNode }) {
  const [registrations, setRegistrations] = useState<readonly FooterRegistration[]>([]);

  const registerFooter = useCallback((ownerId: string, footer: ReactNode | null) => {
    setRegistrations((current) => {
      const withoutOwner = current.filter((entry) => entry.ownerId !== ownerId);
      if (footer === null) {
        return withoutOwner;
      }
      return [...withoutOwner, { ownerId, node: footer }];
    });
  }, []);

  const activeFooter = registrations.at(-1)?.node ?? null;
  const value = useMemo(() => ({ registerFooter }), [registerFooter]);

  return (
    <QuickPanelFooterSlotContext.Provider value={value}>
      <div className="qp-drawer-content">
        {children}
        {activeFooter ? <QuickPanelDrawerFooter>{activeFooter}</QuickPanelDrawerFooter> : null}
      </div>
    </QuickPanelFooterSlotContext.Provider>
  );
}

/** Registers drawer footer actions for the current QuickPanelHost drawer instance. */
export function useQuickPanelFooterSlot(footer: ReactNode | null) {
  const context = useContext(QuickPanelFooterSlotContext);
  const ownerId = useId();

  useLayoutEffect(() => {
    if (!context) {
      return;
    }
    context.registerFooter(ownerId, footer);
    return () => {
      context.registerFooter(ownerId, null);
    };
  }, [context, footer, ownerId]);
}
